import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchPerformanceAggregates,
  fetchSettledBetsPage,
  PerformanceAggregates,
  SettledBetPageRow
} from '../services/betService';
import { fetchProfile } from '../services/profileService';
import { formatTime, formatDate } from '../utils/helpers';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Percent,
  Hash,
  DollarSign,
  Target,
  Trophy,
  X,
  Check,
  Ban
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './Performance.css';

const PAGE_SIZE = 50;

const Performance: React.FC = () => {
  const { user } = useAuth();
  const [aggregates, setAggregates] = useState<PerformanceAggregates | null>(null);
  const [betRows, setBetRows] = useState<SettledBetPageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState('GBP');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  const clientTimeZone = useMemo(
    () => (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'),
    []
  );

  const [listExhausted, setListExhausted] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setLoading(false);
        setAggregates(null);
        setBetRows([]);
        setListExhausted(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setAggregates(null);
        setBetRows([]);
        setListExhausted(false);

        const profilePromise = fetchProfile(user.id);
        const aggregatesPromise = fetchPerformanceAggregates(clientTimeZone, user.id);
        const firstPagePromise = fetchSettledBetsPage(user.id, { limit: PAGE_SIZE, offset: 0 });

        const [profile, agg, firstPage] = await Promise.all([
          profilePromise,
          aggregatesPromise,
          firstPagePromise
        ]);

        if (profile?.currency) {
          setCurrency(profile.currency);
        }

        setAggregates(agg);
        setBetRows(firstPage);
        if (firstPage.length === 0 && agg.allTime.totalBets > 0) {
          setListExhausted(true);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load performance data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.id, clientTimeZone]);

  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      AUD: 'A$',
      CAD: 'C$'
    };
    return symbols[curr] || curr;
  };

  const currencySymbol = getCurrencySymbol(currency);

  const chartData = useMemo(() => {
    if (!aggregates?.daily?.length) {
      return { daily: [] as { date: string; pl: number }[], cumulative: [] as { date: string; cumulativePL: number }[] };
    }

    const dailyData = aggregates.daily
      .map(d => ({
        date: formatDate(d.date_key),
        dateKey: d.date_key,
        pl: parseFloat(Number(d.pl).toFixed(2))
      }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));

    let cumulative = 0;
    const cumulativeData = dailyData.map(day => {
      cumulative += day.pl;
      return {
        date: day.date,
        cumulativePL: parseFloat(Number(cumulative).toFixed(2))
      };
    });

    const dailyPLData = dailyData.map(day => ({
      date: day.date,
      pl: day.pl
    }));

    return {
      daily: dailyPLData,
      cumulative: cumulativeData
    };
  }, [aggregates]);

  const todayStats = aggregates?.today ?? { pl: 0, roi: 0, bets: 0, winRate: 0 };
  const allTimeStats = aggregates?.allTime ?? {
    netPL: 0,
    roi: 0,
    totalBets: 0,
    winRate: 0,
    avgOdds: 0,
    avgStake: 0,
    wins: 0,
    losses: 0
  };

  const totalBets = allTimeStats.totalBets;
  const hasMoreBets = !listExhausted && betRows.length < totalBets;

  const loadNextPage = useCallback(async () => {
    if (!user?.id || loadingMoreRef.current || listExhausted) return;
    if (betRows.length >= totalBets) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const more = await fetchSettledBetsPage(user.id, { limit: PAGE_SIZE, offset: betRows.length });
      if (more.length === 0) {
        setListExhausted(true);
      } else {
        setBetRows(prev => [...prev, ...more]);
      }
    } catch (e) {
      console.error('Failed to load more bets:', e);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [user?.id, betRows.length, totalBets, listExhausted]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || totalBets === 0) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight >= 200) return;
      void loadNextPage();
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [totalBets, betRows.length, loadNextPage]);

  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toFixed(decimals);
  };

  const formatPercent = (num: number, decimals: number = 2): string => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(decimals)}%`;
  };

  const formatCurrency = (num: number): string => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${currencySymbol}${Math.abs(num).toFixed(2)}`;
  };

  const formatBetHistoryDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getResultBadge = (status: string) => {
    switch (status) {
      case 'won':
        return (
          <span className="result-badge won">
            <Check size={14} />
            Won
          </span>
        );
      case 'lost':
        return (
          <span className="result-badge lost">
            <X size={14} />
            Lost
          </span>
        );
      case 'void':
        return (
          <span className="result-badge void">
            <Ban size={14} />
            Void
          </span>
        );
      default:
        return <span className="result-badge">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="performance">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="performance">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!aggregates) {
    return (
      <div className="performance">
        <ErrorMessage message="No performance data available." />
      </div>
    );
  }

  return (
    <div className="performance">
      <div className="performance-header">
        <h1>Performance</h1>
        <p>
          This page shows a history of all the bets you have logged/placed, profit/loss charts and your betting history.
          For a complete history of all the picks and results from our system, visit <Link to="/results">Results History</Link>.
        </p>
      </div>

      <div className="performance-section">
        <h2 className="section-title">Today's Performance</h2>
        <div className="performance-tiles">
          <div className={`summary-tile ${todayStats.pl >= 0 ? 'profit' : 'loss'}`}>
            <div className="summary-icon">
              {todayStats.pl >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
            </div>
            <div className="summary-value">{formatCurrency(todayStats.pl)}</div>
            <div className="summary-label">P/L</div>
          </div>
          <div className={`summary-tile ${todayStats.roi >= 0 ? 'profit' : 'loss'}`}>
            <div className="summary-icon">
              <Percent size={24} />
            </div>
            <div className="summary-value">{formatPercent(todayStats.roi)}</div>
            <div className="summary-label">ROI</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <Hash size={24} />
            </div>
            <div className="summary-value">{todayStats.bets}</div>
            <div className="summary-label">Bets</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <Target size={24} />
            </div>
            <div className="summary-value">{formatPercent(todayStats.winRate)}</div>
            <div className="summary-label">Win Rate</div>
          </div>
        </div>
      </div>

      <div className="performance-section">
        <h2 className="section-title">All Time Performance</h2>
        <div className="performance-tiles all-time">
          <div className={`summary-tile ${allTimeStats.netPL >= 0 ? 'profit' : 'loss'}`}>
            <div className="summary-icon">
              {allTimeStats.netPL >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
            </div>
            <div className="summary-value">{formatCurrency(allTimeStats.netPL)}</div>
            <div className="summary-label">Net P/L</div>
          </div>
          <div className={`summary-tile ${allTimeStats.roi >= 0 ? 'profit' : 'loss'}`}>
            <div className="summary-icon">
              <Percent size={24} />
            </div>
            <div className="summary-value">{formatPercent(allTimeStats.roi)}</div>
            <div className="summary-label">ROI</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <Hash size={24} />
            </div>
            <div className="summary-value">{allTimeStats.totalBets}</div>
            <div className="summary-label">Bets</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <Target size={24} />
            </div>
            <div className="summary-value">{formatPercent(allTimeStats.winRate)}</div>
            <div className="summary-label">Win Rate</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <Trophy size={24} />
            </div>
            <div className="summary-value">{formatNumber(allTimeStats.avgOdds, 2)}</div>
            <div className="summary-label">Avg Odds</div>
          </div>
          <div className="summary-tile">
            <div className="summary-icon">
              <DollarSign size={24} />
            </div>
            <div className="summary-value">
              {currencySymbol}
              {formatNumber(allTimeStats.avgStake, 2)}
            </div>
            <div className="summary-label">Avg Stake</div>
          </div>
          <div className="summary-tile won">
            <div className="summary-icon">
              <Check size={24} />
            </div>
            <div className="summary-value">{allTimeStats.wins}</div>
            <div className="summary-label">Wins</div>
          </div>
          <div className="summary-tile lost">
            <div className="summary-icon">
              <X size={24} />
            </div>
            <div className="summary-value">{allTimeStats.losses}</div>
            <div className="summary-label">Losses</div>
          </div>
        </div>
      </div>

      {chartData.daily.length > 0 && (
        <div className="performance-section">
          <h2 className="section-title">Performance Charts</h2>
          <div className="pl-charts-section">
            <div className="pl-chart-container">
              <h3 className="pl-chart-title">Daily P/L</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis
                    dataKey="date"
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    tickFormatter={(value: number) => Number(value).toFixed(2)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '6px',
                      color: '#f1f5f9'
                    }}
                    formatter={(value: number) => Number(value).toFixed(2)}
                  />
                  <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                  <Line
                    type="monotone"
                    dataKey="pl"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    name="Daily P/L"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="pl-chart-container">
              <h3 className="pl-chart-title">Cumulative P/L</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.cumulative}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis
                    dataKey="date"
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    stroke="#cbd5e1"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    tickFormatter={(value: number) => Number(value).toFixed(2)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '6px',
                      color: '#f1f5f9'
                    }}
                    formatter={(value: number) => Number(value).toFixed(2)}
                  />
                  <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                  <Line
                    type="monotone"
                    dataKey="cumulativePL"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 4 }}
                    name="Cumulative P/L"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="performance-section">
        <h2 className="section-title">Bet History</h2>
        {totalBets === 0 ? (
          <div className="no-bets-message">
            No settled bets found. Your bet history will appear here once bets are settled.
          </div>
        ) : (
          <div className="bet-history-table-container" ref={tableContainerRef}>
            <table className="bet-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Meeting</th>
                  <th>
                    <span className="th-label-full">Race Type</span>
                    <span className="th-label-short">Type</span>
                  </th>
                  <th>Selection</th>
                  <th>Odds</th>
                  <th>Stake</th>
                  <th>
                    <span className="th-label-full">Bookmaker</span>
                    <span className="th-label-short">Bookie</span>
                  </th>
                  <th>Result</th>
                  <th>P/L</th>
                  <th>
                    <span className="th-label-full">Cumulative P/L</span>
                    <span className="th-label-short">CUM P/L</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {betRows.map(bet => {
                  const betPL = bet.effectivePl;
                  return (
                    <tr key={bet.id}>
                      <td>{bet.race?.race_date ? formatBetHistoryDate(bet.race.race_date) : '-'}</td>
                      <td>{bet.race?.race_time ? formatTime(bet.race.race_time) : '-'}</td>
                      <td>{bet.race?.course || '-'}</td>
                      <td>{bet.race?.race_type || '-'}</td>
                      <td className="selection-cell">{bet.runner?.horse_name || '-'}</td>
                      <td>{formatNumber(bet.odds, 2)}</td>
                      <td>
                        {currencySymbol}
                        {formatNumber(bet.stake, 2)}
                      </td>
                      <td>{bet.bookmaker}</td>
                      <td>{getResultBadge(bet.status)}</td>
                      <td className={betPL >= 0 ? 'profit' : 'loss'}>{formatCurrency(betPL)}</td>
                      <td className={bet.cumulativePl >= 0 ? 'profit' : 'loss'}>{formatCurrency(bet.cumulativePl)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMoreBets && (
              <div className="bet-history-load-more">
                {loadingMore ? (
                  <p>Loading more…</p>
                ) : (
                  <p>
                    Showing {betRows.length} of {totalBets} bets. Scroll down to load more.
                  </p>
                )}
              </div>
            )}
            {!hasMoreBets && totalBets > 0 && (
              <div className="bet-history-load-more">
                <p>All {totalBets} bets displayed</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Performance;
